/**
 * MorphViewerPage — Replaces GestureViewer as the default shop viewer.
 *
 * Architecture: morph-viewer-v2.html runs in a fullscreen <iframe>.
 * React overlays sit on top and communicate via postMessage:
 *
 *   iframe → React:
 *     {type:'frame-change', frameIndex}   — frame navigated
 *     {type:'tap', imagePixelX, imagePixelY, frameIndex} — user tapped (disk handled by React)
 *     {type:'mark-item', item}            — user marked an item inside iframe
 *     {type:'ready'}                      — iframe finished loading
 *
 *   React → iframe:
 *     {type:'navigate-to-frame', frameIndex}    — orbit step
 *     {type:'section-info', name, index, total} — section switch
 *
 * Preserved features (unchanged React components):
 *   - FrameItemMarkers  (marked items with 3D projection)
 *   - TapCameraIndicator  (disk blocker + camera discovery + orbit)
 *   - SectionsDrawer  (section/room switching)
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useAppStore } from '@store/appStore'
import { useAuthStore, getAuthHeaders } from '@/stores/authStore'
import { buildMorphUrl, buildMorphUrlFromBase } from '@/utils/buildMorphUrl'
import { buildOrbitSequence } from '@/utils/orbitNavigation'
import { calculateCameraVisibility } from '@/utils/cameraProjection'
import { calculateIndicatorPosition } from './calculateIndicatorPosition'
import { useMarkItem } from '@/hooks/useMarkItem'
import { useCartStore } from '@/stores/cartStore'
import { useStableOffsetMap } from '@/hooks/useConnectedFrames'
import { ShopReviewDrawer } from '@/components/Shops/ShopReviewDrawer'
import { ShopInfoDrawer } from './ShopInfoDrawer'
import { ChatBalloon } from './ChatBalloon'
import { AskPinOverlay } from './AskPinOverlay'
import { TapCameraIndicator } from './TapCameraIndicator'
import { FrameItemMarkers } from './FrameItemMarkers'
import { SectionsDrawer } from '@/components/SectionsDrawer/SectionsDrawer'
import { LoadingProgressBar } from '@/components/LoadingProgressBar/LoadingProgressBar'
import { ItemListPanel } from '@/features/delivery/components/ItemListPanel'
import { CartDrawer } from '@/features/delivery/components/CartDrawer'
import { apiUrl } from '@/config'
import { getGuestId } from '@/utils/guestIdentity'
import { buildSyntheticOffsetMapFromManifest } from '@/utils/offsetMapUtils'
import type { TapIndicatorState } from '@/types/TapIndicatorTypes'
import './GestureViewer.css'

interface MorphViewerPageProps {
  shopId: string
  /** Pre-built morph viewer URL (for uploaded scans). When provided, skips dataset loading. */
  directMorphUrl?: string
}

export function MorphViewerPage({ shopId, directMorphUrl }: MorphViewerPageProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [morphUrl, setMorphUrl] = useState<string | null>(null)
  const manifestUrlRef = useRef<string | null>(null)

  // When a previously-set morphUrl differs from an incoming one ONLY by its SAS
  // token (base identical), swapping `src` reloads the iframe and aborts any
  // in-flight fetches — producing the "[MorphViewer] Network error loading JSON:
  // TypeError: Failed to fetch" we saw when Step 2 and Step 2b each fetched a
  // fresh SAS. This setter treats same-base as a no-op so the iframe keeps
  // using whichever SAS it already has (24h validity — plenty of headroom).
  const _extractBase = (u: string | null): string | null => {
    if (!u) return null
    try {
      const q = u.split('?')[1]
      if (!q) return null
      return new URLSearchParams(q).get('base')
    } catch { return null }
  }
  const setMorphUrlIfBaseChanged = useCallback((next: string | null) => {
    setMorphUrl((prev) => {
      if (!prev || !next) return next
      const a = _extractBase(prev)
      const b = _extractBase(next)
      if (a && b && a === b) return prev
      return next
    })
  }, [])

  const loadManifestForMobileShop = useCallback(async (manifestUrl: string) => {
    try {
      // Try .gz first (replace .json→.json.gz before any query string)
      const gzUrl = manifestUrl.replace('.json', '.json.gz')
      let manifest: any = null
      const gzRes = await fetch(gzUrl)
      if (gzRes.ok) {
        manifest = await gzRes.json()
      } else {
        const res = await fetch(manifestUrl)
        if (res.ok) manifest = await res.json()
      }
      if (!manifest) return
      const offsetMap = buildSyntheticOffsetMapFromManifest(manifest)
      if (offsetMap) {
        useAppStore.setState({ offsetMap })
      }
    } catch (e) {
      console.warn('[MorphViewerPage] manifest load failed:', e)
    }
  }, [])
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0)
  const [tapIndicator, setTapIndicator] = useState<TapIndicatorState | null>(null)
  const [tapIndicatorPannedPosition, setTapIndicatorPannedPosition] = useState<{ x: number; y: number } | null>(null)
  const visitNotifiedRef = useRef(false)
  const directShopLoadedRef = useRef(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [zenMode, setZenMode] = useState(false)
  const authUser = useAuthStore((s) => s.user)

  // "Ask about this" pin-placement flow
  const [askPin, setAskPin] = useState<{ x: number; y: number; r: number } | null>(null)
  const [chatPin, setChatPin] = useState<
    { frameIndex: number; imagePixelX: number; imagePixelY: number; radius: number; sectionIndex: number }
    | null
  >(null)

  // Asset deep-link from Messages chip: ?frame=N&px=...&py=...&pr=...
  // Persists across re-renders so we can navigate the iframe to that frame
  // on first 'ready' and show a non-interactive green circle on top.
  const assetTargetRef = useRef<{ frame: number | null; px: number | null; py: number | null; pr: number | null } | null>(null)
  const [assetPin, setAssetPin] = useState<{ frame: number; px: number; py: number; pr: number } | null>(null)
  // Parse URL once on mount
  useEffect(() => {
    const u = new URLSearchParams(window.location.search)
    const frame = u.get('frame') != null ? parseInt(u.get('frame')!, 10) : null
    const px = u.get('px') != null ? parseFloat(u.get('px')!) : null
    const py = u.get('py') != null ? parseFloat(u.get('py')!) : null
    const pr = u.get('pr') != null ? parseFloat(u.get('pr')!) : null
    if (px != null && py != null) {
      assetTargetRef.current = { frame, px, py, pr }
      if (frame != null) setAssetPin({ frame, px, py, pr: pr ?? 40 })
    }
  }, [])


  // Store selectors
  const cameras = useAppStore((s) => s.cameras)
  const sections = useAppStore((s) => s.sections)
  const shopDatasets = useAppStore((s) => s.shopDatasets)
  const canUseTapIndicator = useAppStore((s) => s.canUseTapIndicator)
  const isLoadingData = useAppStore((s) => s.isLoadingData)
  const loadingProgress = useAppStore((s) => s.loadingProgress)
  const loadingStage = useAppStore((s) => s.loadingStage)
  const currentDatasetId = useAppStore((s) => s.currentDatasetId)
  const currentSectionFramesPath = useAppStore((s) => s.currentSectionFramesPath)
  const manuallySelectedFrameIndex = useAppStore((s) => s.manuallySelectedFrameIndex)
  const loadDataset = useAppStore((s) => s.loadDataset)
  const availableDatasets = useAppStore((s) => s.availableDatasets)

  // 3D offset map for camera ranking
  const rawOffsetMap = useAppStore((s) => s.offsetMap)
  const offsetMap = useStableOffsetMap(rawOffsetMap)

  // Marked items hook + panel state
  const { handleMarkItem, itemListOpen, setItemListOpen, cartDrawerOpen, setCartDrawerOpen } = useMarkItem({ shopId })

  // Count only items for the current shop + section (matches what ItemListPanel shows)
  const currentSectionId = useAppStore((s) => s.currentSectionId)
  const allCartItems = useCartStore((s) => s.items)
  const visibleItemCount = allCartItems.filter(
    (i) => i.shopId === shopId && i.sectionId === String(currentSectionId)
  ).length

  // Current frame camera
  const frameCamera = cameras[currentFrameIndex] ?? cameras[0]

  // Display dimensions (full viewport since iframe is fullscreen)
  const displayWidth = window.innerWidth
  const displayHeight = window.innerHeight

  // ── Load a mobile-uploaded shop via public API + blob URLs ──────────────────
  //
  // Stage-1 fast-pan: we commit shop metadata + morph URL as soon as the
  // public-shop response arrives. The iframe itself fetches cameras.json /
  // points3d.json and shares the parsed data back via the `scan-data-ready`
  // postMessage (handled below) — so React never fetches those files.
  const loadMobileShop = useCallback(async (mobileShopId: string, base: string, sas: string) => {
    useAppStore.getState().setLoadingData(true)
    try {
      const shopRes = await fetch(apiUrl(`/api/shops/public/${encodeURIComponent(mobileShopId)}`))
      if (!shopRes.ok) { directShopLoadedRef.current = true; useAppStore.getState().setLoadingData(false); return }
      const shopData = await shopRes.json()
      const remoteSections: Array<{ sectionId: string; name?: string }> = shopData?.sections ?? []

      const appendSas = (url: string) => sas ? `${url}${url.includes('?') ? '&' : '?'}${sas}` : url
      const shopDatasets = remoteSections.map((sec, i) => {
        const secBase = `https://vshopship.blob.core.windows.net/scan-frames/${mobileShopId}/sections/${sec.sectionId}/`
        return {
          id: `${mobileShopId}_${sec.sectionId}`,
          shopId: mobileShopId,
          sectionId: sec.sectionId,
          sectionName: sec.name ?? `Section ${i + 1}`,
          name: sec.name ?? `Section ${i + 1}`,
          video: '', experiment: '', reconstruction_id: '', camera_count: 0,
          success_rate: '', quality_score: 0, description: '', matcher: '', resolution: '', fps: 0,
          paths: {
            cameras: appendSas(`${secBase}cameras.json`),
            points3d: appendSas(`${secBase}points3d.json`),
            points3d_simplified: appendSas(`${secBase}points3d.json`),
            frames: appendSas(`${secBase}frames/`),
            manifest: appendSas(`${secBase}manifest.json`),
          },
          source: { video_path: '', colmap_output: '' },
        }
      })

      // Store first section's manifest URL so scan-data-ready can load it
      if (remoteSections[0]) {
        const firstBase = `https://vshopship.blob.core.windows.net/scan-frames/${mobileShopId}/sections/${remoteSections[0].sectionId}/`
        manifestUrlRef.current = appendSas(`${firstBase}manifest.json`)
      }

      // Use the provided base (from directMorphUrl), or fall back to the first section's base
      const firstSection = remoteSections[0]
      const resolvedBase = base || (firstSection
        ? `https://vshopship.blob.core.windows.net/scan-frames/${mobileShopId}/sections/${firstSection.sectionId}/`
        : '')

      // Build the morph URL; the iframe will handle cameras/points fetching.
      if (resolvedBase) {
        const morphParams = new URLSearchParams({ base: resolvedBase, ...(sas ? { sas } : {}) })
        setMorphUrl(`/morph-viewer/morph-viewer-v2.html?${morphParams.toString()}`)
      }

      directShopLoadedRef.current = true
      useAppStore.setState({
        currentDatasetId: mobileShopId,
        currentShopId: mobileShopId,
        shopDatasets,
        activeUserSectionId: firstSection?.sectionId ?? null,
        isLoadingData: false,
        // cameras/points3D populated by the `scan-data-ready` bridge below.
      })
    } catch (e) {
      console.error('[MorphViewerPage] loadMobileShop failed:', e)
      useAppStore.getState().setLoadingData(false)
    }
  }, [])

  // ── Step 1: Load dataset into Zustand store ──────────────────────────────────
  useEffect(() => {
    // ── directMorphUrl path (legacy ?morphUrl= format) ──
    if (directMorphUrl) {
      const urlParams = new URLSearchParams(directMorphUrl.split('?')[1] ?? '')
      const base = urlParams.get('base') ?? ''
      const sas = urlParams.get('sas') ?? ''
      const extractedShopId = base.match(/scan-frames\/(shop_[^/]+)\//)?.[1]
      setMorphUrl(directMorphUrl)
      if (!extractedShopId || directShopLoadedRef.current) return
      loadMobileShop(extractedShopId, base, sas)
      return
    }

    if (!shopId || shopId === currentDatasetId) return

    // ── shopId path: check if it's in the manifest first ──
    const { availableDatasets: ads } = useAppStore.getState()
    const inManifest = ads.some(d => d.id === shopId || d.shopId === shopId)
    if (inManifest || ads.length === 0) {
      // Standard manifest-based shop OR datasets not loaded yet — use loadDataset
      const urlParams = new URLSearchParams(window.location.search)
      const targetSection = urlParams.get('section') ? parseInt(urlParams.get('section')!, 10) : undefined
      const targetFrame = urlParams.get('frame') ? parseInt(urlParams.get('frame')!, 10) : undefined
      useAppStore.getState().setLoadingData(true)
      loadDataset(shopId, { section: targetSection, frame: targetFrame })
    } else {
      // Mobile-uploaded shop: not in manifest — use public shop API + blob URLs
      if (directShopLoadedRef.current) return
      loadMobileShop(shopId, '', '')
    }
  }, [shopId, currentDatasetId, loadDataset, directMorphUrl, availableDatasets, loadMobileShop])

  // ── Step 2: Build morph iframe URL from dataset manifest ─────────────────────
  useEffect(() => {
    if (directMorphUrl || !shopId) return
    const dataset = availableDatasets.find((d) => d.id === shopId) ?? { id: shopId }
    buildMorphUrl(dataset)
      .then(setMorphUrlIfBaseChanged)
      .catch(() => {
        setMorphUrlIfBaseChanged(`/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(`/api/scan-data/${shopId}/section_0/`)}`)
      })
  }, [shopId, availableDatasets, directMorphUrl, setMorphUrlIfBaseChanged])

  // ── Step 2b: Rebuild morph URL from the correct section after loadDataset ────
  useEffect(() => {
    if (directMorphUrl || !currentSectionFramesPath || currentDatasetId !== shopId) return
    const framesDir = currentSectionFramesPath.endsWith('/frames/')
      ? currentSectionFramesPath.slice(0, -'frames/'.length)
      : currentSectionFramesPath.replace(/\/frames\/?$/, '/')
    buildMorphUrlFromBase(framesDir)
      .then(setMorphUrlIfBaseChanged)
      .catch(console.error)
  }, [currentSectionFramesPath, currentDatasetId, shopId, directMorphUrl, setMorphUrlIfBaseChanged])

  // ── Step 3: Track shop visit (same as GestureViewer) ─────────────────────────
  useEffect(() => {
    if (!shopId || !currentDatasetId || visitNotifiedRef.current) return
    visitNotifiedRef.current = true
    const authUser = useAuthStore.getState().user
    const visitorId = authUser?.id || getGuestId()
    const visitorName = authUser?.name || undefined
    fetch(apiUrl(`/api/shops/${encodeURIComponent(shopId)}/visit`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ visitorId, visitorName }),
    }).catch(() => {})
    import('../../services/activityService').then(({ trackVisit }) => { trackVisit(shopId) })
  }, [shopId, currentDatasetId])

  // ── Step 4: postMessage bridge ────────────────────────────────────────────────
  const sendToIframe = useCallback((msg: object) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*')
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return
      let msg: any
      try { msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data } catch { return }

      switch (msg.type) {
        case 'ready': {
          // Asset deep-link: hop straight to the asked frame.
          const target = assetTargetRef.current
          if (target?.frame != null) {
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ type: 'navigate-to-frame', frameIndex: target.frame }),
              '*',
            )
            setCurrentFrameIndex(target.frame)
          }
          break
        }

        case 'scan-data-ready': {
          // Iframe finished parsing cameras.json. Read the parsed data across
          // the same-origin iframe boundary. points3d arrives later via tap-ready.
          try {
            const scanData = (iframeRef.current?.contentWindow as unknown as { __scanData?: { cameras: unknown[]; points: unknown[] } } | null)?.__scanData
            if (!scanData?.cameras?.length) break
            useAppStore.setState({
              cameras: scanData.cameras as never,
              points3D: (scanData.points ?? []) as never,
              // canUseTapIndicator set later when tap-ready arrives (points3d loaded)
              isLoadingData: false,
            })
            // Load manifest for mobile-uploaded scans so offsetMap is available
            // for tap indicator and frame filtering (COLMAP datasets load it via appStore)
            if (manifestUrlRef.current) {
              loadManifestForMobileShop(manifestUrlRef.current)
            }
          } catch { /* ignore */ }
          break
        }

        case 'tap-ready': {
          // points3d.json finished loading in the iframe — enable tap indicator
          useAppStore.setState({ canUseTapIndicator: true })
          break
        }

        case 'frame-change':
          if (typeof msg.frameIndex === 'number') {
            setCurrentFrameIndex(msg.frameIndex)
            setTapIndicator(null) // dismiss disk on manual navigation
          }
          break

        case 'tap': {
          if (!canUseTapIndicator || !cameras.length) break
          const { imagePixelX, imagePixelY, frameIndex: tapFrame = currentFrameIndex } = msg
          const cam = cameras[tapFrame] ?? cameras[0]
          if (!cam) break

          const imgW = cam.intrinsics.width
          const imgH = cam.intrinsics.height

          // Use image dimensions as the container rect (same pattern as NewModeViewer shared URL tap)
          const fakeRect = new DOMRect(0, 0, imgW, imgH)
          const points3D = useAppStore.getState().points3D
          const { highlightedCameras, projections, point3D } = calculateCameraVisibility(
            imagePixelX, imagePixelY, fakeRect, cam, cameras, points3D, offsetMap, tapFrame
          )

          // Scale image-space coords to display coords
          const displayX = (imagePixelX / imgW) * displayWidth
          const displayY = (imagePixelY / imgH) * displayHeight

          // Build orbit sequence
          if (point3D) {
            const orbit = buildOrbitSequence(point3D, projections, cameras, tapFrame)
            const store = useAppStore.getState()
            store.setOrbitSequence?.(orbit)
            store.setOrbitIndex?.(0)
          }

          setTapIndicatorPannedPosition(null)
          setTapIndicator({
            show: true,
            cameraCount: highlightedCameras.size,
            screenPosition: { x: displayX, y: displayY },
            frameRelativeTapX: imagePixelX,
            frameRelativeTapY: imagePixelY,
            imagePixelX,
            imagePixelY,
            currentDisplayX: displayX,
            currentDisplayY: displayY,
            projections,
            point3D,
            frameCamera: cam,
            frameIndex: tapFrame,
            tappedFrameIndex: tapFrame,
            is2DOnly: !point3D,
          })
          break
        }

        case 'zen-mode':
          setZenMode(!!msg.active)
          break

        case 'mark-item': {
          // Morph viewer marked an item natively — save to CartStore
          if (msg.item) {
            const { imagePixelX, imagePixelY, frameIndex, point3D } = msg.item
            handleMarkItem({
              imagePixelX: imagePixelX ?? 0,
              imagePixelY: imagePixelY ?? 0,
              radius: 30,
              point3D: point3D ?? null,
              projections: new Map(),
              frameIndex: frameIndex ?? currentFrameIndex,
            })
          }
          break
        }
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [cameras, canUseTapIndicator, currentFrameIndex, displayWidth, displayHeight, offsetMap, handleMarkItem])

  // ── Orbit helpers ─────────────────────────────────────────────────────────────
  const handleOrbitStep = useCallback((targetFrameIndex: number) => {
    sendToIframe({ type: 'navigate-to-frame', frameIndex: targetFrameIndex })
    setCurrentFrameIndex(targetFrameIndex)
    // Update tap indicator position for new frame
    if (tapIndicator) {
      const cam = cameras[targetFrameIndex]
      if (!cam) return
      const proj = tapIndicator.projections.get(targetFrameIndex)
      if (!proj) return
      const [dx, dy] = calculateIndicatorPosition(proj, cam.intrinsics.width, cam.intrinsics.height, displayWidth, displayHeight)
      setTapIndicator((prev) => prev ? { ...prev, frameIndex: targetFrameIndex, frameCamera: cam, currentDisplayX: dx, currentDisplayY: dy } : null)
      setTapIndicatorPannedPosition(null)
    }
  }, [sendToIframe, tapIndicator, cameras, displayWidth, displayHeight])

  const handleOrbitPrev = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const prevIdx = (idx - 1 + seq.length) % seq.length
    store.setOrbitIndex(prevIdx)
    handleOrbitStep(seq[prevIdx].frameIndex)
  }, [handleOrbitStep])

  const handleOrbitNext = useCallback(() => {
    const store = useAppStore.getState()
    const seq = store.orbitSequence
    const idx = store.orbitIndex
    if (!seq || seq.length < 2) return
    const nextIdx = (idx + 1) % seq.length
    store.setOrbitIndex(nextIdx)
    handleOrbitStep(seq[nextIdx].frameIndex)
  }, [handleOrbitStep])

  // ── Forward ItemListPanel frame navigation to iframe ─────────────────────────
  useEffect(() => {
    if (manuallySelectedFrameIndex == null) return
    sendToIframe({ type: 'navigate-to-frame', frameIndex: manuallySelectedFrameIndex })
    setCurrentFrameIndex(manuallySelectedFrameIndex)
  }, [manuallySelectedFrameIndex, sendToIframe])

  // ── Desktop arrow key navigation (when iframe doesn't have focus) ─────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        sendToIframe({ type: 'keydown', key: e.key })
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sendToIframe])

  // ── TapCameraIndicator derived values ─────────────────────────────────────────
  const indicatorDisplayX = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[0] : 0

  const indicatorDisplayY = tapIndicator ? calculateIndicatorPosition(
    [tapIndicator.imagePixelX, tapIndicator.imagePixelY],
    frameCamera?.intrinsics?.width ?? 450,
    frameCamera?.intrinsics?.height ?? 800,
    displayWidth,
    displayHeight,
  )[1] : 0

  const indicatorPosition = tapIndicatorPannedPosition ?? { x: indicatorDisplayX, y: indicatorDisplayY }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="gesture-viewer" style={{ position: 'fixed', inset: 0, background: '#000' }}>
      {/* Loading state while morph URL is being fetched */}
      {!morphUrl && (
        <div className="gesture-viewer__loading">
          <button
            className="gesture-viewer__cancel-btn"
            onClick={() => window.location.href = '/'}
            title="Cancel"
          >✕</button>
          <div className="spinner" />
          <p>Loading shop...</p>
        </div>
      )}

      {/* Section-switch progress bar */}
      {isLoadingData && canUseTapIndicator && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 500, pointerEvents: 'none' }}>
          <LoadingProgressBar progress={loadingProgress} stage={loadingStage || 'Loading section...'} />
        </div>
      )}

      {/* ── The morph viewer iframe ── */}
      {morphUrl && (
        <iframe
          ref={iframeRef}
          src={morphUrl}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            pointerEvents: (reviewOpen || infoOpen || chatOpen || itemListOpen || cartDrawerOpen) ? 'none' : 'auto',
          }}
          allow="fullscreen"
          title="Shop Viewer"
        />
      )}

      {/* ── React overlays on top of iframe ── */}

      {/* Marked item circles + flags */}
      {frameCamera && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          <FrameItemMarkers
            frameIndex={currentFrameIndex}
            frameCamera={frameCamera}
            frameDisplayWidth={displayWidth}
            frameDisplayHeight={displayHeight}
            shopId={shopId}
          />
        </div>
      )}

      {/* TapCameraIndicator — disk blocker + orbit + discovery */}
      {canUseTapIndicator && tapIndicator && tapIndicator.show && frameCamera && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'none' }}>
            <TapCameraIndicator
              cameraCount={tapIndicator.cameraCount}
              position={indicatorPosition}
              currentDisplayX={indicatorDisplayX}
              currentDisplayY={indicatorDisplayY}
              frameRelativeTapX={tapIndicator.frameRelativeTapX}
              frameRelativeTapY={tapIndicator.frameRelativeTapY}
              imagePixelX={tapIndicator.imagePixelX}
              imagePixelY={tapIndicator.imagePixelY}
              frameCamera={frameCamera}
              frameIndex={currentFrameIndex}
              cameras={cameras}
              points3D={useAppStore.getState().points3D}
              offsetMap={offsetMap}
              frameDisplayWidth={displayWidth}
              radiusPxImageSpace={tapIndicator.is2DOnly ? undefined : tapIndicator.radiusPxImageSpace}
              scaleData={tapIndicator.is2DOnly ? undefined : tapIndicator.scaleData}
              zoomScale={1}
              onShowThumbnails={() => {}}
              onPanComplete={(_finalScreenX, _finalScreenY, finalDisplayX, finalDisplayY) => {
                setTapIndicatorPannedPosition({ x: finalDisplayX, y: finalDisplayY })
                // Convert display position back to image pixel for new discovery origin
                const cam = frameCamera
                const newImagePixelX = (finalDisplayX / displayWidth) * (cam?.intrinsics?.width ?? 450)
                const newImagePixelY = (finalDisplayY / displayHeight) * (cam?.intrinsics?.height ?? 800)
                setTapIndicator((prev) => prev ? { ...prev, imagePixelX: newImagePixelX, imagePixelY: newImagePixelY } : null)
              }}
              onClose={() => setTapIndicator(null)}
              shopId={shopId}
              is2DOnly={tapIndicator.is2DOnly}
              onMarkItem={() => {
                handleMarkItem({
                  imagePixelX: tapIndicator.imagePixelX,
                  imagePixelY: tapIndicator.imagePixelY,
                  radius: tapIndicator.is2DOnly ? 30 : (tapIndicator.radiusPxImageSpace ?? 30),
                  point3D: tapIndicator.point3D,
                  projections: tapIndicator.projections,
                  frameIndex: tapIndicator.frameIndex,
                })
                setTapIndicator(null)
                setItemListOpen(true)
              }}
              onOrbitPrev={handleOrbitPrev}
              onOrbitNext={handleOrbitNext}
            />
          </div>
        </div>
      )}

      {/* SectionsDrawer — section/room switching */}
      {canUseTapIndicator && !zenMode && (sections.length > 0 || shopDatasets.length > 1) && (
        <SectionsDrawer />
      )}

      {/* Top-left floating toolbar (back, review, info, ask) — single flex row */}
      <div
        style={{
          position: 'fixed',
          top: 'calc(12px + env(safe-area-inset-top, 0px))',
          left: 12,
          zIndex: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: 4,
          background: 'rgba(20,20,20,0.78)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 14,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          opacity: zenMode ? 0 : 1,
          pointerEvents: zenMode ? 'none' : 'auto',
          transform: zenMode ? 'translateX(-18px) scale(0.9)' : 'none',
          transition: 'opacity 0.28s ease, transform 0.28s ease',
        }}
      >
        <button onClick={() => window.history.back()} style={toolbarBtn('#fff')} title="Back">‹</button>
        <span style={toolbarDivider} />
        <button onClick={() => setReviewOpen(true)} style={toolbarBtn('#f59e0b')} title="Reviews">★</button>
        <button
          onClick={() => setInfoOpen(true)}
          style={{ ...toolbarBtn('#60a5fa'), fontStyle: 'italic', fontFamily: 'serif', fontWeight: 700 }}
          title="Shop info"
        >i</button>
        <button
          onClick={() => setAskPin({ x: displayWidth / 2, y: displayHeight / 2, r: 60 })}
          style={{
            ...toolbarBtn('#34d399'),
            display: askPin || chatOpen ? 'none' : 'flex',
          }}
          title="Ask about something in this view"
        >?</button>
      </div>

      {/* Floating bag button — below back arrow, top-left */}
      {visibleItemCount > 0 && (
        <button
          onClick={() => setItemListOpen(true)}
          style={{
            position: 'fixed',
            top: 'calc(72px + env(safe-area-inset-top, 0px))',
            left: 16,
            zIndex: 600,
            background: 'rgba(20,20,20,0.85)',
            border: '1.5px solid rgba(255,255,255,0.25)',
            borderRadius: 12,
            color: '#fff',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            opacity: zenMode ? 0 : 1,
            pointerEvents: zenMode ? 'none' : 'auto',
            transform: zenMode ? 'translateX(-18px) scale(0.88)' : 'none',
            transition: 'opacity 0.28s ease, transform 0.28s ease',
            justifyContent: 'center',
            gap: 4,
            fontSize: 18,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
          }}
          title="View marked items"
        >
          🛍️
          <span style={{ fontSize: 12, fontWeight: 700 }}>{visibleItemCount}</span>
        </button>
      )}

      {/* Asset target circle — read-only highlight when opened via a chat asset chip */}
      {assetPin && frameCamera && currentFrameIndex === assetPin.frame && (() => {
        const imgW = frameCamera.intrinsics?.width ?? displayWidth
        const imgH = frameCamera.intrinsics?.height ?? displayHeight
        const cx = (assetPin.px / imgW) * displayWidth
        const cy = (assetPin.py / imgH) * displayHeight
        const r = (assetPin.pr / imgW) * displayWidth
        return (
          <>
            <div
              style={{
                position: 'fixed',
                left: cx - r,
                top: cy - r,
                width: r * 2,
                height: r * 2,
                borderRadius: '50%',
                border: '3px solid #34d399',
                boxShadow: '0 0 22px rgba(52,211,153,0.7)',
                background: 'rgba(52,211,153,0.10)',
                pointerEvents: 'none',
                zIndex: 590,
              }}
            />
            <button
              onClick={() => setAssetPin(null)}
              style={{
                position: 'fixed',
                left: cx + r + 6,
                top: cy - r - 6,
                width: 26, height: 26,
                borderRadius: '50%',
                background: 'rgba(20,20,20,0.85)',
                border: '1.5px solid rgba(52,211,153,0.6)',
                color: '#86efac',
                fontSize: 14,
                cursor: 'pointer',
                zIndex: 595,
                lineHeight: 1,
                padding: 0,
              }}
              title="Hide asset highlight"
            >✕</button>
          </>
        )
      })()}

      {/* AskPin overlay — draggable + resizable circle */}
      {askPin && frameCamera && (
        <AskPinOverlay
          pin={askPin}
          setPin={setAskPin}
          displayWidth={displayWidth}
          displayHeight={displayHeight}
          onCancel={() => setAskPin(null)}
          onConfirm={() => {
            const imgW = frameCamera.intrinsics?.width ?? displayWidth
            const imgH = frameCamera.intrinsics?.height ?? displayHeight
            const imagePixelX = (askPin.x / displayWidth) * imgW
            const imagePixelY = (askPin.y / displayHeight) * imgH
            const radius = (askPin.r / displayWidth) * imgW
            const idx = shopDatasets.findIndex((d: any) => String(d.sectionId) === String(currentSectionId))
            setChatPin({
              frameIndex: currentFrameIndex,
              imagePixelX,
              imagePixelY,
              radius,
              sectionIndex: idx < 0 ? 0 : idx,
            })
            setAskPin(null)
            setChatOpen(true)
          }}
        />
      )}

      {reviewOpen && (
        <ShopReviewDrawer
          shopId={shopId}
          onClose={() => setReviewOpen(false)}
        />
      )}

      {infoOpen && (
        <ShopInfoDrawer
          shopId={shopId}
          onClose={() => setInfoOpen(false)}
          onChat={() => setChatOpen(true)}
        />
      )}

      {chatOpen && (
        <ChatBalloon
          shopId={shopId}
          frameIndex={chatPin?.frameIndex ?? currentFrameIndex}
          imagePixelX={chatPin?.imagePixelX}
          imagePixelY={chatPin?.imagePixelY}
          radius={chatPin?.radius}
          sectionIndex={chatPin?.sectionIndex}
          initialPin={chatPin ? {
            datasetId: shopId,
            sectionIndex: chatPin.sectionIndex,
            frameIndex: chatPin.frameIndex,
            imagePixelX: chatPin.imagePixelX,
            imagePixelY: chatPin.imagePixelY,
            radius: chatPin.radius,
          } : null}
          anchorX={displayWidth / 2}
          anchorY={displayHeight / 2}
          viewportWidth={displayWidth}
          viewportHeight={displayHeight}
          userId={authUser?.id ?? null}
          onClose={() => { setChatOpen(false); setChatPin(null) }}
        />
      )}

      {/* Marked items panel + cart drawer */}
      <ItemListPanel
        isOpen={itemListOpen}
        onClose={() => setItemListOpen(false)}
        onOpenCart={() => { setItemListOpen(false); setCartDrawerOpen(true) }}
        shopId={shopId}
      />
      <CartDrawer isOpen={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />
    </div>
  )
}

export default MorphViewerPage

const toolbarBtn = (color: string): React.CSSProperties => ({
  width: 38,
  height: 38,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 10,
  color,
  fontSize: 20,
  cursor: 'pointer',
  padding: 0,
  WebkitTapHighlightColor: 'transparent',
})

const toolbarDivider: React.CSSProperties = {
  width: 1,
  height: 22,
  background: 'rgba(255,255,255,0.15)',
  margin: '0 2px',
}
